/**
 * Order detail (web) — Phase 5 marketplace.
 *
 * Reads a single order (GET /orders/:id — buyer or seller) and renders its full
 * line: buyer, seller, product, qty, unit price + total, status, and dates.
 *
 * Actions, gated by role + status:
 *   - "Mark settled"  → PATCH /orders/:id/settle. Shown ONLY when the caller is
 *       the SELLER and the order is still `draft` (the server enforces both;
 *       this just hides a button the caller can't use).
 *   - "Cancel order"  → PATCH /orders/:id/cancel (confirm dialog). Shown to
 *       EITHER party while the order is `draft`.
 *
 * Caller-is-seller is derived by comparing the access-token `sub` against
 * `sellerUserId` (which can be null for a coop-listing order — then no single
 * user is "the seller" and the settle button is hidden client-side; the server
 * remains the authority).
 *
 * MONEY: unit price + total are micro-ZARP (1e-6 ZARP) STRINGS — render via
 * microToZarp() + <Money>, never Number() for math. A 404/403 is treated as a
 * friendly "not found" (the caller shouldn't learn whether an order they're not
 * party to exists), not a hard error.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Icon,
  Money,
  Skeleton,
  Stack,
  StatusPill,
  Stepper,
  Text,
  type StatusPillVariant,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import {
  cancelOrder,
  fetchDeliveryPhotoObjectUrl,
  getOrder,
  settleOrder,
  type OrderDto,
  type OrderStatus,
} from '../api/orders';
import { microToZarp } from '../api/market';

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

// Full settlement lifecycle the API exposes (escrowed..refunded land with
// TD-126); the off-platform MVP only ever reaches draft/settled/cancelled today.
const ORDER_PILL: Record<OrderStatus, StatusPillVariant> = {
  draft: 'due',
  escrowed: 'active',
  delivered: 'active',
  attested: 'active',
  settled: 'sold',
  disputed: 'overdue',
  refunded: 'overdue',
  cancelled: 'overdue',
};

function errMsg(err: unknown): string {
  return err instanceof FseApiError ? err.message : String(err);
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

// --------------------------------------------------------------------------
// Lifecycle stepper model
//
// The happy-path lifecycle is Created → Escrowed → Delivered → Settled. Each
// status maps to how many of those four steps are COMPLETE (so the canonical
// @fse/ui Stepper, which marks steps before `activeStep` as done and `activeStep`
// itself as in-progress, lands the right ticks):
//   draft     → Created done, awaiting the rest        → activeStep 1 (Escrowed)
//   escrowed  → through Escrowed                       → activeStep 2 (Delivered)
//   delivered → through Delivered                      → activeStep 3 (Settled)
//   attested  → through Delivered (settle imminent)    → activeStep 3 (Settled)
//   settled   → all four complete                      → activeStep 4 (past last)
// Terminal-FAILURE states (cancelled/refunded/disputed) don't ride the happy
// path — they render a distinct banner instead of the stepper (see TERMINAL).
const STEP_KEYS = ['created', 'escrowed', 'delivered', 'settled'] as const;

const STATUS_ACTIVE_STEP: Record<OrderStatus, number> = {
  draft: 1,
  escrowed: 2,
  delivered: 3,
  attested: 3,
  settled: 4,
  // Terminal-failure: a banner replaces the stepper, but keep a sane index.
  disputed: 3,
  refunded: 4,
  cancelled: 1,
};

// Terminal states shown distinctly (no happy-path stepper). The banner severity
// signals success vs failure; `settled` is terminal-success and keeps the
// stepper (all four ticked), so it is NOT listed here.
const TERMINAL_FAILURE: Partial<Record<OrderStatus, 'error' | 'warning'>> = {
  cancelled: 'warning',
  refunded: 'warning',
  disputed: 'error',
};

export function OrderDetailScreen() {
  const { t } = useTranslation();
  // Route is wired manually in router.ts (path '/orders/$id'); read params
  // loosely (strict: false) — no router-plugin route-id literal is reachable here.
  const { id } = useParams({ strict: false }) as { id?: string };
  const orderId = id ?? '';

  const orderQuery = useQuery({
    queryKey: ['order', orderId],
    queryFn: ({ signal }) => getOrder(orderId, signal),
    enabled: orderId.length > 0,
    // 404 (no such order) / 403 (not a party) are stable "not found" states,
    // not transient — don't retry them.
    retry: (failureCount, error) => {
      if (
        error instanceof FseApiError &&
        (error.status === 404 || error.status === 403)
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const isNotFound =
    orderQuery.isError &&
    orderQuery.error instanceof FseApiError &&
    (orderQuery.error.status === 404 || orderQuery.error.status === 403);

  return (
    <Stack spacing={3}>
      <Box>
        <Link to="/orders" style={{ textDecoration: 'none' }}>
          <Button variant="text" size="small" startIcon={<Icon name="arrow_back" size={18} />}>
            {t('orderDetail.back')}
          </Button>
        </Link>
      </Box>

      {orderQuery.isPending ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5}>
              <Skeleton variant="text" width={220} height={40} />
              <Skeleton variant="rectangular" height={200} />
            </Stack>
          </CardContent>
        </Card>
      ) : isNotFound ? (
        <Card>
          <CardHeader title={t('orderDetail.notFound.title')} />
          <CardContent>
            <Text variant="body-medium" color="text.secondary">
              {t('orderDetail.notFound.body')}
            </Text>
          </CardContent>
        </Card>
      ) : orderQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => orderQuery.refetch()}>
              {t('orderDetail.retry')}
            </Button>
          }
        >
          {t('orderDetail.loadError', { error: errMsg(orderQuery.error) })}
        </Alert>
      ) : (
        <OrderDetailBody order={orderQuery.data} t={t} />
      )}
    </Stack>
  );
}

interface OrderDetailBodyProps {
  order: OrderDto;
  t: TFunction;
}

function OrderDetailBody({ order, t }: OrderDetailBodyProps) {
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);

  const callerSub = readAccessTokenClaims()?.sub ?? null;
  const callerIsSeller =
    callerSub !== null && order.sellerUserId !== null && order.sellerUserId === callerSub;

  const isDraft = order.status === 'draft';
  const unitPrice = microToZarp(order.unitPriceZarpPerKgMicro);
  const total = microToZarp(order.totalZarpMicro);

  function invalidateOrder() {
    queryClient.invalidateQueries({ queryKey: ['order', order.id] });
    // The list view's status/total can change too.
    queryClient.invalidateQueries({ queryKey: ['orders'] });
  }

  const settleMutation = useMutation({
    mutationFn: () => settleOrder(order.id),
    onSuccess: invalidateOrder,
  });
  const cancelMutation = useMutation({
    mutationFn: () => cancelOrder(order.id),
    onSuccess: () => {
      setCancelOpen(false);
      invalidateOrder();
    },
  });

  const sellerLabel = order.sellerName ?? order.sellerCoopName ?? '—';
  // A settle button only makes sense for the seller on a draft order; cancel is
  // open to either party while draft.
  const canSettle = callerIsSeller && isDraft;
  const canCancel = isDraft;

  return (
    <Stack spacing={3}>
      {/* Header card: id + status + total */}
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Text variant="title-large" as="h2">
                  {order.product ?? t('orderDetail.unknownProduct')}
                </Text>
                <StatusPill variant={ORDER_PILL[order.status]}>
                  {t(`orderDetail.status.${order.status}`)}
                </StatusPill>
              </Stack>
              <Text variant="body-medium" color="text.secondary">
                {t('orderDetail.orderRef', { ref: shortId(order.id) })}
                {' · '}
                {t('orderDetail.placed', {
                  date: dateTimeFormatter.format(new Date(order.createdAt)),
                })}
              </Text>
            </Stack>
            <Box sx={{ textAlign: 'right' }}>
              <Text variant="label-medium" color="text.secondary">
                {t('orderDetail.total')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={total} size="lg" />
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Lifecycle: a distinct banner for terminal-failure states, else the
          Created → Escrowed → Delivered → Settled progress stepper. */}
      <OrderLifecycle order={order} t={t} />

      {/* Delivery-recorded: proof photo + when + where (only once delivered). */}
      {order.deliveredAt ? <DeliverySection order={order} t={t} /> : null}

      {/* Line detail */}
      <Card variant="outlined">
        <CardHeader title={t('orderDetail.detailsHeading')} />
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <DetailField label={t('orderDetail.buyer')} value={order.buyerName ?? '—'} />
            <DetailField label={t('orderDetail.seller')} value={sellerLabel} />
            <DetailField
              label={t('orderDetail.product')}
              value={order.product ?? t('orderDetail.unknownProduct')}
            />
            <DetailField label={t('orderDetail.quantity')} value={t('orderDetail.qty', { qty: order.qtyKg })} />
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('orderDetail.unitPrice')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={unitPrice} unit={t('orderDetail.perKg')} size="sm" />
              </Box>
            </Box>
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('orderDetail.total')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={total} size="sm" />
              </Box>
            </Box>
            {order.settledAt ? (
              <DetailField
                label={t('orderDetail.settledAt')}
                value={dateTimeFormatter.format(new Date(order.settledAt))}
              />
            ) : null}
            {order.expiresAt ? (
              <DetailField
                label={t('orderDetail.expiresAt')}
                value={dateTimeFormatter.format(new Date(order.expiresAt))}
              />
            ) : null}
          </Box>

          {/* Actions */}
          {canSettle || canCancel ? (
            <>
              <Divider sx={{ my: 2 }} />

              {settleMutation.isError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {t('orderDetail.actionError', { error: errMsg(settleMutation.error) })}
                </Alert>
              ) : null}
              {settleMutation.isSuccess ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t('orderDetail.settledOk')}
                </Alert>
              ) : null}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                {canCancel ? (
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => setCancelOpen(true)}
                    disabled={settleMutation.isPending || cancelMutation.isPending}
                  >
                    {t('orderDetail.cancel')}
                  </Button>
                ) : null}
                {canSettle ? (
                  <Button
                    variant="contained"
                    onClick={() => settleMutation.mutate()}
                    disabled={settleMutation.isPending || cancelMutation.isPending}
                    startIcon={
                      settleMutation.isPending ? <CircularProgress size={16} /> : <Icon name="check_circle" size={18} />
                    }
                  >
                    {t('orderDetail.settle')}
                  </Button>
                ) : null}
              </Box>
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Cancel confirm */}
      <Dialog
        open={cancelOpen}
        onClose={() => {
          if (cancelMutation.isPending) return;
          cancelMutation.reset();
          setCancelOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('orderDetail.cancelConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('orderDetail.cancelConfirm.body')}</DialogContentText>
          {cancelMutation.isError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t('orderDetail.actionError', { error: errMsg(cancelMutation.error) })}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => {
              cancelMutation.reset();
              setCancelOpen(false);
            }}
            disabled={cancelMutation.isPending}
          >
            {t('orderDetail.cancelConfirm.keep')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            startIcon={cancelMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t('orderDetail.cancelConfirm.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * Order lifecycle visual. Terminal-failure orders (cancelled/refunded/disputed)
 * show a distinct banner — they never travelled the happy path, so a stepper
 * would imply forward progress. Every other order renders the four-step
 * Created → Escrowed → Delivered → Settled progress stepper, with reached steps
 * ticked off the current status and per-step timestamps surfaced underneath
 * where the DTO carries them.
 */
function OrderLifecycle({ order, t }: { order: OrderDto; t: TFunction }) {
  const terminalSeverity = TERMINAL_FAILURE[order.status];

  if (terminalSeverity) {
    return (
      <Card variant="outlined">
        <CardHeader title={t('orderDetail.lifecycle.heading')} />
        <CardContent>
          <Alert severity={terminalSeverity} icon={<Icon name="error" size={20} />}>
            {t(`orderDetail.lifecycle.terminal.${order.status}`)}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const steps = STEP_KEYS.map((k) => t(`orderDetail.lifecycle.step.${k}`));
  const activeStep = STATUS_ACTIVE_STEP[order.status];

  // Per-step timestamps where the DTO carries them: Created (createdAt),
  // Delivered (deliveredAt), Settled (settledAt). Escrowed has no dedicated
  // timestamp in the off-platform MVP, so it is left blank.
  const stamps: Array<string | null> = [
    order.createdAt ? dateTimeFormatter.format(new Date(order.createdAt)) : null,
    null,
    order.deliveredAt ? dateTimeFormatter.format(new Date(order.deliveredAt)) : null,
    order.settledAt ? dateTimeFormatter.format(new Date(order.settledAt)) : null,
  ];

  return (
    <Card variant="outlined">
      <CardHeader title={t('orderDetail.lifecycle.heading')} />
      <CardContent>
        <Box sx={{ pt: 0.5 }}>
          <Stepper steps={steps} activeStep={activeStep} />
        </Box>
        {/* Timestamps in the same 4-up grid so each sits under its dot. */}
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1,
          }}
        >
          {stamps.map((stamp, i) => (
            <Text
              key={STEP_KEYS[i]}
              variant="label-small"
              color="text.secondary"
              align="center"
            >
              {stamp ?? ''}
            </Text>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Delivery-recorded section — only mounted once `deliveredAt` is set. Shows the
 * proof-of-delivery photo (authed bytes → object URL, see DeliveryPhoto), the
 * delivered date/time, and the captured GPS point (null-safe: coordinates may
 * not have been captured even when delivery was recorded).
 */
function DeliverySection({ order, t }: { order: OrderDto; t: TFunction }) {
  const hasCoords =
    typeof order.deliveryLat === 'number' && typeof order.deliveryLng === 'number';
  // OSM is link-only (no embed/token) — opens the captured point in a new tab.
  const mapHref = hasCoords
    ? `https://www.openstreetmap.org/?mlat=${order.deliveryLat}&mlon=${order.deliveryLng}#map=15/${order.deliveryLat}/${order.deliveryLng}`
    : null;
  const coordsLabel = hasCoords
    ? t('orderDetail.delivery.coords', {
        lat: (order.deliveryLat as number).toFixed(5),
        lng: (order.deliveryLng as number).toFixed(5),
      })
    : null;

  return (
    <Card variant="outlined">
      <CardHeader title={t('orderDetail.delivery.heading')} />
      <CardContent>
        <Stack spacing={2}>
          {/* Show the photo only when the order says it has one. */}
          {order.hasDeliveryPhoto ? (
            <DeliveryPhoto orderId={order.id} t={t} />
          ) : null}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <DetailField
              label={t('orderDetail.delivery.deliveredAt')}
              value={
                order.deliveredAt
                  ? dateTimeFormatter.format(new Date(order.deliveredAt))
                  : '—'
              }
            />
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('orderDetail.delivery.location')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                {coordsLabel ? (
                  <Stack spacing={0.25}>
                    <Text variant="body-large" as="span">
                      {coordsLabel}
                    </Text>
                    {mapHref ? (
                      <Box
                        component="a"
                        href={mapHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: 'primary.main',
                          textDecoration: 'none',
                          width: 'fit-content',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <Icon name="my_location" size={16} color="inherit" />
                        <Text variant="label-medium" as="span" color="inherit">
                          {t('orderDetail.delivery.viewMap')}
                        </Text>
                      </Box>
                    ) : null}
                  </Stack>
                ) : (
                  <Text variant="body-large" as="span" color="text.secondary">
                    {t('orderDetail.delivery.noLocation')}
                  </Text>
                )}
              </Box>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

/**
 * Proof-of-delivery photo. The endpoint streams bytes behind auth, so a plain
 * <img src> won't send the bearer — we fetch with the Authorization header →
 * blob → object URL (src/api/orders.ts → fetchDeliveryPhotoObjectUrl), mirroring
 * how web shows a harvest photo. The object URL is revoked on unmount /
 * id-change to avoid leaking blobs (cancelled-guard for in-flight fetches).
 */
function DeliveryPhoto({ orderId, t }: { orderId: string; t: TFunction }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    setFailed(false);
    setObjectUrl(null);

    fetchDeliveryPhotoObjectUrl(orderId)
      .then((u) => {
        if (cancelled) {
          // Unmounted / id changed mid-flight — revoke the orphaned blob.
          URL.revokeObjectURL(u);
          return;
        }
        url = u;
        setObjectUrl(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [orderId]);

  const frameSx = {
    width: '100%',
    maxWidth: 360,
    aspectRatio: '4 / 3',
    borderRadius: 1,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'action.hover',
    color: 'text.disabled',
  } as const;

  if (failed) {
    return (
      <Box sx={frameSx}>
        <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
          <Icon name="error" size={28} color="inherit" />
          <Text variant="label-small" color="text.secondary">
            {t('orderDetail.delivery.photoError')}
          </Text>
        </Stack>
      </Box>
    );
  }

  if (!objectUrl) {
    return (
      <Box sx={frameSx}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={frameSx}>
      <Box
        component="img"
        src={objectUrl}
        alt={t('orderDetail.delivery.photoAlt')}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text variant="label-medium" color="text.secondary">
        {label}
      </Text>
      <Box sx={{ mt: 0.25 }}>
        <Text variant="body-large" as="span">
          {value}
        </Text>
      </Box>
    </Box>
  );
}
